import { readonly, ref, useContext } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import { getAvailablePaymentMethodsCommand } from '~/modules/checkout/composables/usePaymentProvider/commands/getAvailablePaymentMethodsCommand';
import useCart from '~/modules/checkout/composables/useCart';

import type { Cart } from '~/modules/GraphQL/types';
import { CustomQuery, CustomHeaders } from '~/types/core';
import setPaymentMethodOnCart from '~/modules/GraphQL/customQueries/setPaymentMethodOnCart.gql';
import type {
  UsePaymentProviderErrors,
  UsePaymentProviderInterface,
  UsePaymentProviderSaveParams,
  PaymentMethodParams,
} from './usePaymentProvider';

/**
 * Allows loading the available payment
 * methods for current cart, and selecting (saving) one of them.
 *
 * See the {@link UsePaymentProviderInterface} for a list of methods and values available in this composable.
 */
export function usePaymentProvider(): UsePaymentProviderInterface {
  const context = useContext();
  const { cart, setCart } = useCart();
  const loading = ref(false);
  const error = ref<UsePaymentProviderErrors>({
    load: null,
    save: null,
  });

  const save = async (params: UsePaymentProviderSaveParams) => {
    Logger.debug('usePaymentProvider.save');
    let result = null;

    try {
      loading.value = true;
      const paymentMethodParams: PaymentMethodParams = {
        cart_id: cart.value.id,
        payment_method: {
          ...params.paymentMethod,
        },
        customQuery: params.customQuery,
        customHeaders: params?.customHeaders,
      };
      const paymentMethodInput = {
        input: paymentMethodParams,
      };
      const { data } = await context.app.$vsf.$magento.api.customQuery({
        query: setPaymentMethodOnCart,
        queryVariables: paymentMethodInput,
      });
      // @ts-ignore
      const cartData: Cart = data?.setPaymentMethodOnCart?.cart;
      setCart(cartData);

      result = cartData.available_payment_methods ?? [];
      error.value.save = null;
    } catch (err) {
      error.value.save = err;
      Logger.error('usePaymentProvider/save', err);
    } finally {
      loading.value = false;
    }

    Logger.debug('[Result]:', { result });
    return result;
  };

  const load = async (customQuery?: CustomQuery, customHeaders?: CustomHeaders) => {
    Logger.debug('usePaymentProvider.load');
    let result = null;

    try {
      loading.value = true;
      result = await getAvailablePaymentMethodsCommand.execute(context, cart.value.id, customQuery, customHeaders);
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('usePaymentProvider/load', err);
    } finally {
      loading.value = false;
    }

    Logger.debug('[Result]:', { result });
    return result;
  };

  return {
    load,
    save,
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from './usePaymentProvider';
export default usePaymentProvider;
